import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import WorkflowBuilder from './WorkflowBuilder';
import CaseDocumentUploader from './CaseDocumentUploader';
import CaseAnalysisPanel from './CaseAnalysisPanel';
import StrategyBrain from './StrategyBrain';
import ResponseDetection from './ResponseDetection';
import ComplaintPortal from './ComplaintPortal';
import MailingRoom from './MailingRoom';
import { 
  Send, 
  Paperclip, 
  Loader2, 
  User, 
  Bot, 
  Copy, 
  ThumbsUp, 
  ThumbsDown,
  RefreshCw,
  FileText,
  MessageSquare,
  Scale,
  BookOpen,
  CheckCircle,
  Circle,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Upload,
  X,
  Lightbulb,
  Gavel,
  ScrollText,
  Target,
  FileCheck,
  History,
  Brain,
  Shield,
  Mail
} from 'lucide-react';
import axios from 'axios';

const ChatInterface = () => {
  const { conversationId } = useParams();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [attorneyMessages, setAttorneyMessages] = useState([]);
  const [activeTab, setActiveTab] = useState('workflow');
  const [strategyInitialView, setStrategyInitialView] = useState('overview');
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConversation, setIsLoadingConversation] = useState(true);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [caseState, setCaseState] = useState({
    intakeComplete: false,
    issueFramed: false,
    remedySuggested: false,
    currentPhase: 'intake'
  });
  const [expandedSections, setExpandedSections] = useState({
    actionItems: true,
    caseTimeline: false,
    whyMatters: {}
  });
  const [isAnalyzingDocument, setIsAnalyzingDocument] = useState(false);
  const [caseDocCount, setCaseDocCount] = useState(0);
  const [rightPanel, setRightPanel] = useState('status'); // 'status' | 'documents'
  const [strategyRefreshTrigger, setStrategyRefreshTrigger] = useState(0);
  const [hasRemedy, setHasRemedy] = useState(false);
  const [generatingRemedy, setGeneratingRemedy] = useState(null);
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPaid = user && user.subscription_plan !== 'free';
  const { toast } = useToast();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const docAnalysisInputRef = useRef(null);

  const caseType = conversation?.metadata?.case_type || '';
  const isPetitioner = caseType === 'petitioner';
  const isDebtDischarge = caseType === 'debt_discharge';
  const textareaRef = useRef(null);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (conversationId) {
      fetchConversation();
    } else {
      setIsLoadingConversation(false);
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [attorneyMessages]);

  const fetchConversation = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/conversations/${conversationId}`);
      setConversation(response.data);
      setMessages(response.data.messages || []);
      
      // Load attorney mode messages from metadata if available
      if (response.data.metadata?.attorney_messages) {
        setAttorneyMessages(response.data.metadata.attorney_messages);
      }
      if (response.data.metadata?.action_items) {
        setActionItems(response.data.metadata.action_items);
      }
      if (response.data.metadata?.case_state) {
        setCaseState(response.data.metadata.case_state);
      }
      // Check if remedy has been generated
      if (response.data.metadata?.remedy_content || response.data.metadata?.generated_documents) {
        setHasRemedy(true);
      }
    } catch (error) {
      console.error('Failed to fetch conversation:', error);
      toast({
        title: "Error",
        description: "Failed to load conversation",
        variant: "destructive"
      });
    } finally {
      setIsLoadingConversation(false);
    }

    // Load case document count
    try {
      const token = localStorage.getItem('token');
      const docRes = await axios.get(`${API_BASE}/api/attorney/case-documents/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCaseDocCount(docRes.data.documents?.length || 0);
    } catch {}
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleDocumentSaved = (newDocument) => {
    toast({
      title: "Document Saved",
      description: `"${newDocument.title}" has been saved to your Document Vault`,
    });
  };

  // Attorney Mode specific handlers
  const handleAttorneySubmit = async (e) => {
    e.preventDefault();
    
    if ((!inputValue.trim() && attachedFiles.length === 0) || isLoading) return;
    
    if (!conversationId) {
      toast({
        title: "No Conversation",
        description: "Please start a new case matter first",
        variant: "destructive"
      });
      return;
    }

    const userText = inputValue.trim();
    setInputValue('');
    setIsLoading(true);

    // Build the full message: user text + any attached document content
    let fullMessage = userText;
    const currentAttachments = [...attachedFiles];

    if (currentAttachments.length > 0) {
      const docsWithText = currentAttachments.filter(f => f.extractedText);
      if (docsWithText.length > 0) {
        let documentContent = '\n\n--- Attached Document(s) ---\n';
        for (const doc of docsWithText) {
          documentContent += `\n--- ${doc.name} ---\n${doc.extractedText}\n`;
        }
        if (documentContent.length > 60000) {
          documentContent = documentContent.substring(0, 60000) + '\n\n[Document truncated due to length]';
        }
        fullMessage = (userText || 'Please review the attached document(s).') + documentContent;
      }
    }

    // Add user message to UI immediately
    const newUserMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: userText || `Attached ${currentAttachments.length} file(s)`,
      timestamp: new Date().toISOString(),
      attachedFiles: currentAttachments.map(f => ({ name: f.name, type: f.type }))
    };
    setAttorneyMessages(prev => [...prev, newUserMessage]);
    setAttachedFiles([]);

    try {
      // Build case context from conversation
      const caseContext = {
        case_type: caseType,
        category: conversation?.metadata?.category,
        client_info: conversation?.metadata?.client_info,
        case_description: conversation?.metadata?.case_description,
        opposing_party: conversation?.metadata?.opposing_party_info?.fullName
      };

      const response = await axios.post(
        `${API_BASE}/api/attorney/chat/${conversationId}`,
        {
          message: fullMessage,
          case_context: caseContext,
          attached_files: currentAttachments.map(f => ({ name: f.name, type: f.type, size: f.size }))
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        // Add AI response
        const aiMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: response.data.response,
          timestamp: response.data.timestamp,
          actionItems: response.data.action_items
        };
        setAttorneyMessages(prev => [...prev, aiMessage]);

        // Update action items
        if (response.data.action_items?.length > 0) {
          setActionItems(prev => [...prev, ...response.data.action_items]);
        }

        // Save to conversation metadata
        await saveAttorneyState([...attorneyMessages, newUserMessage, aiMessage], actionItems);

      } else {
        throw new Error(response.data.error || 'Failed to get response');
      }
    } catch (error) {
      console.error('Attorney mode error:', error);
      toast({
        title: "Error",
        description: typeof error.response?.data?.detail === 'string' ? error.response.data.detail : "Failed to process your request",
        variant: "destructive"
      });
      setInputValue(userText);
    } finally {
      setIsLoading(false);
    }
  };

  const saveAttorneyState = async (messages, items) => {
    try {
      await axios.put(
        `${API_BASE}/api/conversations/${conversationId}`,
        {
          metadata: {
            ...conversation?.metadata,
            attorney_messages: messages.slice(-50), // Keep last 50 messages
            action_items: items,
            case_state: caseState
          }
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
    } catch (error) {
      console.error('Failed to save attorney state:', error);
    }
  };

  const handleAttachmentClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileAttachment = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    if (!conversationId) {
      toast({ title: "No Conversation", description: "Please start a new case matter first before attaching files.", variant: "destructive" });
      event.target.value = '';
      return;
    }

    const maxSize = 15 * 1024 * 1024;
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'image/jpeg',
      'image/png'
    ];

    const validFiles = [];
    for (const file of files) {
      if (file.size > maxSize) {
        toast({ title: "File Too Large", description: `${file.name} exceeds 15MB limit`, variant: "destructive" });
        continue;
      }
      if (!allowedTypes.includes(file.type)) {
        toast({ title: "Invalid File Type", description: `${file.name} is not supported. Use PDF, DOCX, TXT, JPG, PNG`, variant: "destructive" });
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) { event.target.value = ''; return; }

    const token = localStorage.getItem('token');

    setIsLoading(true);

    // Upload each file and collect extracted text — but do NOT auto-send to AI
    const newAttachments = [];
    for (const file of validFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('tag', 'other');
        formData.append('description', '');
        const res = await axios.post(
          `${API_BASE}/api/attorney/case-documents/${conversationId}/upload`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        newAttachments.push({
          id: res.data.doc_id || `file-${Date.now()}-${file.name}`,
          name: file.name,
          type: file.type,
          size: file.size,
          extractedText: res.data.extracted_text || '',
        });
      } catch (err) {
        console.error('File upload error:', err.response?.status, err.response?.data);
        toast({ title: `Failed: ${file.name}`, description: typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Upload error', variant: 'destructive' });
      }
    }

    if (newAttachments.length > 0) {
      setAttachedFiles(prev => [...prev, ...newAttachments]);
      toast({
        title: `${newAttachments.length} file(s) attached`,
        description: 'Type your message and hit Send.',
      });
      // Trigger Strategy Brain auto-refresh for new documents
      setStrategyRefreshTrigger(prev => prev + 1);
    } else {
      toast({ title: 'Upload failed', description: 'Could not attach file(s). Please try again.', variant: 'destructive' });
    }

    setIsLoading(false);
    event.target.value = '';
  };

  const removeAttachedFile = (fileId) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleGenerateRemedy = async (remedyType) => {
    if (!conversationId || generatingRemedy) return;
    setGeneratingRemedy(remedyType);

    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(
        `${API_BASE}/api/attorney/generate-remedy/${conversationId}`,
        { remedy_type: remedyType },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const remedyMsg = {
        id: Date.now().toString(),
        role: 'assistant',
        content: `**${remedyType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}**\n\n> *AI-generated document for educational purposes. Not legal advice.*\n\n${res.data.document}`,
        timestamp: new Date().toISOString(),
        isRemedyDoc: true,
        remedyType: remedyType,
      };
      setAttorneyMessages(prev => [...prev, remedyMsg]);
      toast({ title: 'Remedy Document Generated' });
    } catch (err) {
      toast({ title: 'Generation failed', description: typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Please try again.', variant: 'destructive' });
    } finally {
      setGeneratingRemedy(null);
    }
  };

  const handleDocumentAnalysis = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsAnalyzingDocument(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('case_context', JSON.stringify({
        case_type: caseType,
        category: conversation?.metadata?.category
      }));

      const response = await axios.post(
        `${API_BASE}/api/attorney/upload-and-analyze/${conversationId}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        // Add analysis as AI message
        const analysisMessage = {
          id: Date.now().toString(),
          role: 'assistant',
          content: response.data.analysis,
          timestamp: response.data.timestamp,
          isDocumentAnalysis: true,
          documentName: response.data.document_name
        };
        setAttorneyMessages(prev => [...prev, analysisMessage]);

        toast({
          title: "Document Analyzed",
          description: `Analysis complete for ${file.name}`,
        });
      }
    } catch (error) {
      console.error('Document analysis error:', error);
      toast({
        title: "Analysis Failed",
        description: typeof error.response?.data?.detail === 'string' ? error.response.data.detail : "Failed to analyze document",
        variant: "destructive"
      });
    } finally {
      setIsAnalyzingDocument(false);
      event.target.value = '';
    }
  };

  const toggleActionItem = (itemId) => {
    setActionItems(prev => prev.map(item => 
      item.id === itemId 
        ? { ...item, status: item.status === 'completed' ? 'pending' : 'completed' }
        : item
    ));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAttorneySubmit(e);
    }
  };

  // Start a new Attorney Mode conversation
  const handleNewAttorneyConversation = async () => {
    try {
      // Clear the attorney session on backend
      await axios.delete(
        `${API_BASE}/api/attorney/session/${conversationId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      // Clear local state
      setAttorneyMessages([]);
      setActionItems([]);
      setCaseState({
        intakeComplete: false,
        issueFramed: false,
        remedySuggested: false,
        currentPhase: 'intake'
      });

      // Save cleared state to conversation
      await axios.put(
        `${API_BASE}/api/conversations/${conversationId}`,
        {
          metadata: {
            ...conversation?.metadata,
            attorney_messages: [],
            action_items: [],
            case_state: {
              intakeComplete: false,
              issueFramed: false,
              remedySuggested: false,
              currentPhase: 'intake'
            }
          }
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      toast({
        title: "New Conversation Started",
        description: "Your Attorney Mode conversation has been reset. Start fresh!",
      });
    } catch (error) {
      console.error('Error starting new conversation:', error);
      // Still clear local state even if API fails
      setAttorneyMessages([]);
      setActionItems([]);
      toast({
        title: "Conversation Reset",
        description: "Starting fresh conversation",
      });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: "Message copied to clipboard",
    });
  };

  const renderMessage = (msg) => {
    const isUser = msg.role === 'user';
    
    return (
      <div key={msg.id} className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          isUser ? 'bg-indigo-600' : 'bg-gradient-to-br from-amber-500 to-amber-700'
        }`}>
          {isUser ? (
            <User className="w-4 h-4 text-white" />
          ) : (
            <Scale className="w-4 h-4 text-white" />
          )}
        </div>
        
        <div className={`flex-1 max-w-[85%] ${isUser ? 'text-right' : ''}`}>
          <div className={`inline-block p-4 rounded-2xl ${
            isUser 
              ? 'bg-indigo-600 text-white' 
              : 'bg-white border border-gray-200 shadow-sm'
          }`}>
            {msg.isDocumentAnalysis && (
              <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-200">
                <FileCheck className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-700">
                  Document Analysis: {msg.documentName}
                </span>
              </div>
            )}
            
            <div className={`prose prose-sm max-w-none ${isUser ? 'prose-invert' : ''}`}>
              {msg.content.split('\n').map((line, i) => {
                // Strip all markdown asterisks from the line for clean display
                const clean = (text) => text.replace(/\*{1,3}/g, '');

                // Check for "Why This Matters" sections
                if (line.includes('💡 Why This Matters') || line.includes('Why This Matters')) {
                  return (
                    <div key={i} className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center gap-2 text-amber-700 font-medium mb-1">
                        <Lightbulb className="w-4 h-4" />
                        Why This Matters
                      </div>
                      <p className="text-amber-800 text-sm">{clean(line.replace(/💡\s*Why This Matters:?/gi, '').trim())}</p>
                    </div>
                  );
                }
                // Handle headers
                if (line.startsWith('##')) {
                  return <h3 key={i} className="font-bold text-base mt-3 mb-2">{clean(line.replace(/##\s*/, ''))}</h3>;
                }
                if (line.startsWith('**') && line.endsWith('**')) {
                  return <p key={i} className="font-semibold mt-2">{clean(line)}</p>;
                }
                // Handle bullet points
                if (line.startsWith('- ') || line.startsWith('• ')) {
                  return <li key={i} className="ml-4">{clean(line.substring(2))}</li>;
                }
                // Handle numbered items
                if (/^\d+\.\s/.test(line)) {
                  return <li key={i} className="ml-4 list-decimal">{clean(line.replace(/^\d+\.\s/, ''))}</li>;
                }
                return line ? <p key={i} className="mb-1">{clean(line)}</p> : <br key={i} />;
              })}
            </div>

            {msg.attachedFiles?.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <span className="text-xs text-gray-500">
                  Attached: {msg.attachedFiles.map(f => f.name).join(', ')}
                </span>
              </div>
            )}

            {msg.isAnalyzing && (
              <div className="flex items-center gap-2 mt-2 text-amber-600">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Analyzing...</span>
              </div>
            )}

            {msg.isDeepAnalysis && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2">Generate Remedy Documents:</p>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { key: 'general_rebuttal', label: 'Rebuttal' },
                    { key: 'remedy_plan', label: 'Remedy Plan' },
                    { key: 'affidavit_of_truth', label: 'Affidavit of Truth' },
                    { key: 'conditional_acceptance', label: 'Conditional Acceptance' },
                    { key: 'notice_to_cure', label: 'Notice to Cure' },
                    { key: 'notice_of_fault_default', label: 'Notice of Fault' },
                    { key: 'demand_verified_claim', label: 'Demand Verified Claim' },
                    { key: 'cease_and_desist', label: 'Cease & Desist' },
                    { key: 'administrative_rebuttal', label: 'Admin Rebuttal' },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => handleGenerateRemedy(key)}
                      disabled={!!generatingRemedy}
                      className="text-[11px] px-2 py-1 rounded-full border border-amber-300 text-amber-700 hover:bg-amber-50 transition disabled:opacity-50"
                      data-testid={`remedy-chat-${key}`}
                    >
                      {generatingRemedy === key ? 'Generating...' : label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {msg.isRemedyDoc && (
              <div className="mt-2 pt-2 border-t border-gray-200 flex gap-2">
                <button
                  onClick={() => copyToClipboard(msg.content)}
                  className="text-[11px] px-2 py-1 rounded border text-gray-600 hover:bg-gray-50"
                >
                  Copy
                </button>
                <button
                  onClick={() => {
                    const blob = new Blob([msg.content], { type: 'text/markdown' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${msg.remedyType || 'remedy'}.md`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="text-[11px] px-2 py-1 rounded border text-gray-600 hover:bg-gray-50"
                >
                  Download
                </button>
              </div>
            )}
          </div>
          
          {!isUser && (
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={() => copyToClipboard(msg.content)}
                className="p-1 hover:bg-gray-100 rounded"
                title="Copy"
              >
                <Copy className="w-3 h-3 text-gray-400" />
              </button>
              <span className="text-xs text-gray-400">
                {new Date(msg.timestamp).toLocaleTimeString()}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Welcome message for Attorney Mode
  const renderWelcomeMessage = () => (
    <div className="flex gap-3">
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
        <Scale className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1">
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="p-4">
            <h3 className="font-bold text-amber-900 text-lg mb-2">
              ⚖️ Your Private Attorney is Ready
            </h3>
            <p className="text-amber-800 text-sm mb-4">
              I'm your intelligent legal assistant specializing in Equity Jurisprudence and Constitutional Law. 
              I'm here to help you navigate your legal matter with honor and proper process.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div className="bg-white/70 p-3 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 text-amber-700 font-medium mb-1">
                  <Target className="w-4 h-4" />
                  Case Intake
                </div>
                <p className="text-xs text-amber-600">Tell me about your legal situation and I'll ask guided questions</p>
              </div>
              
              <div className="bg-white/70 p-3 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 text-amber-700 font-medium mb-1">
                  <ScrollText className="w-4 h-4" />
                  Issue Framing
                </div>
                <p className="text-xs text-amber-600">I'll frame your issue using Equity principles</p>
              </div>
              
              <div className="bg-white/70 p-3 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 text-amber-700 font-medium mb-1">
                  <Gavel className="w-4 h-4" />
                  Remedy Steps
                </div>
                <p className="text-xs text-amber-600">Get a logical sequence of actions to resolve your matter</p>
              </div>
              
              <div className="bg-white/70 p-3 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 text-amber-700 font-medium mb-1">
                  <FileCheck className="w-4 h-4" />
                  Document Review
                </div>
                <p className="text-xs text-amber-600">Upload documents for equity alignment check</p>
              </div>
            </div>

            <div className="bg-amber-100 p-3 rounded-lg">
              <p className="text-amber-800 text-sm">
                <strong>💡 Start by telling me:</strong> What legal matter do you need help with today? 
                Describe your situation and I'll guide you through the process.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  if (isLoadingConversation) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-indigo-600" />
          <p className="text-gray-600">Loading conversation...</p>
        </div>
      </div>
    );
  }

  if (!conversationId) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center max-w-md">
          <Bot className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Welcome to TheEqualizer.AI
          </h2>
          <p className="text-gray-600 mb-6">
            Start a new case matter to get AI-powered legal assistance. Click "New Case Matter" in the sidebar to begin.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full overflow-x-hidden">
      {/* Mode Selection Tabs */}
      <div className="border-b bg-white px-4 py-2">
        <div className="flex justify-center">
          <div className="grid grid-cols-6 max-w-3xl w-full bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('workflow')}
              className={`flex items-center justify-center gap-1 px-2 py-2 rounded-md text-xs font-medium transition-all ${
                activeTab === 'workflow'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileText className="w-3.5 h-3.5" />
              Workflow
            </button>
            <button
              onClick={() => setActiveTab('mailing')}
              className={`flex items-center justify-center gap-1 px-2 py-2 rounded-md text-xs font-medium transition-all ${
                activeTab === 'mailing'
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="mailing-room-tab"
            >
              <Mail className="w-3.5 h-3.5" />
              Mailing
            </button>
            <button
              onClick={() => setActiveTab('detection')}
              className={`flex items-center justify-center gap-1 px-2 py-2 rounded-md text-xs font-medium transition-all ${
                activeTab === 'detection'
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="response-detection-tab"
            >
              <Shield className="w-3.5 h-3.5" />
              Detection
            </button>
            <button
              onClick={() => {
                if (hasRemedy) {
                  setActiveTab('complaint');
                } else {
                  toast({ title: 'Remedy Required', description: 'Please generate a remedy on the Workflow tab first before accessing the Complaint Portal.', variant: 'destructive' });
                }
              }}
              className={`flex items-center justify-center gap-1 px-2 py-2 rounded-md text-xs font-medium transition-all ${
                hasRemedy
                  ? activeTab === 'complaint'
                    ? 'bg-white text-blue-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 cursor-pointer'
                  : 'text-gray-300 cursor-not-allowed'
              }`}
              data-testid="complaint-portal-tab"
            >
              <Gavel className="w-3.5 h-3.5" />
              Complaint
            </button>
            <button
              onClick={() => setActiveTab('strategy')}
              className={`flex items-center justify-center gap-1 px-2 py-2 rounded-md text-xs font-medium transition-all ${
                activeTab === 'strategy'
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="strategy-brain-tab"
            >
              <Brain className="w-3.5 h-3.5" />
              Strategy
            </button>
            <button
              onClick={() => setActiveTab('chat')}
              className={`flex items-center justify-center gap-1 px-2 py-2 rounded-md text-xs font-medium transition-all ${
                activeTab === 'chat'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Scale className="w-3.5 h-3.5" />
              Attorney
            </button>
          </div>
        </div>
      </div>

      {/* WorkflowBuilder - Always mounted */}
      <div className={`overflow-auto h-[calc(100vh-200px)] ${activeTab === 'workflow' ? 'block' : 'hidden'}`}>
        <WorkflowBuilder 
          conversationId={conversationId} 
          user={user} 
          onDocumentSaved={handleDocumentSaved}
          onSwitchToStrategy={() => { setStrategyInitialView('map'); setActiveTab('strategy'); }}
        />
      </div>

      {/* Enhanced Attorney Mode Interface */}
      {activeTab === 'chat' && (
        <div className="flex flex-1 overflow-hidden">
          {/* Main Chat Area */}
          <div className="flex-1 flex flex-col">
            {/* Attorney Mode Header */}
            <div className="border-b bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Scale className="w-5 h-5 text-amber-600" />
                <span className="font-medium text-amber-900">Private Attorney Session</span>
                {attorneyMessages.length > 0 && (
                  <Badge variant="outline" className="text-xs bg-white">
                    {attorneyMessages.length} messages
                  </Badge>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNewAttorneyConversation}
                className="text-amber-700 border-amber-300 hover:bg-amber-100"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                New Conversation
              </Button>
            </div>

            {/* Chat Messages */}
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4 max-w-4xl mx-auto">
                {attorneyMessages.length === 0 && renderWelcomeMessage()}
                {attorneyMessages.map(renderMessage)}
                
                {isLoading && (
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
                      <Scale className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                        <span className="text-gray-600">Analyzing your matter...</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Input Area */}
            <div className="border-t bg-white p-4">
              <form onSubmit={handleAttorneySubmit} className="max-w-4xl mx-auto">
                {/* Attached Files Display */}
                {attachedFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {attachedFiles.map((file) => (
                      <div key={file.id} className="flex items-center gap-1 bg-amber-50 text-amber-700 px-3 py-1 rounded-full text-sm border border-amber-200">
                        <FileText className="w-3 h-3" />
                        <span className="max-w-[150px] truncate">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeAttachedFile(file.id)}
                          className="ml-1 hover:text-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2 items-end">
                  {/* Message Input */}
                  <div className="relative flex-1">
                    <Textarea
                      ref={textareaRef}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Describe your legal matter or ask a question..."
                      className="min-h-[50px] max-h-[200px] pr-12 resize-none"
                      disabled={isLoading}
                    />
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                      onChange={handleFileAttachment}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={`absolute right-2 bottom-2 h-8 w-8 p-0 ${attachedFiles.length > 0 ? 'text-amber-600' : ''}`}
                      disabled={isLoading}
                      onClick={handleAttachmentClick}
                      title="Attach documents"
                    >
                      <Paperclip className="w-4 h-4" />
                      {attachedFiles.length > 0 && (
                        <span className="absolute -top-1 -right-1 bg-amber-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                          {attachedFiles.length}
                        </span>
                      )}
                    </Button>
                  </div>

                  {/* Send Button */}
                  <Button
                    type="submit"
                    disabled={(!inputValue.trim() && attachedFiles.length === 0) || isLoading}
                    className="h-12 w-12 p-0 bg-amber-600 hover:bg-amber-700"
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </Button>
                </div>

                <p className="text-xs text-gray-500 mt-2 text-center">
                  Attorney Mode provides legal education and document assistance, not legal advice. 
                  Consult qualified counsel for specific situations.
                </p>
              </form>
            </div>
          </div>

          {/* Right Sidebar - Action Items & Case State */}
          <div className="w-80 border-l bg-gray-50 overflow-y-auto hidden lg:block">
            <div className="p-4 space-y-4">

              {/* Action Items */}
              <Card>
                <CardHeader className="py-3 px-4">
                  <button
                    onClick={() => setExpandedSections(prev => ({ ...prev, actionItems: !prev.actionItems }))}
                    className="w-full flex items-center justify-between"
                  >
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Action Items ({actionItems.filter(i => i.status === 'pending').length})
                    </CardTitle>
                    {expandedSections.actionItems ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                </CardHeader>
                {expandedSections.actionItems && (
                  <CardContent className="px-4 pb-4">
                    {actionItems.length === 0 ? (
                      <p className="text-xs text-gray-500">No action items yet. Start a conversation to get recommendations.</p>
                    ) : (
                      <div className="space-y-2">
                        {actionItems.map((item) => (
                          <div
                            key={item.id}
                            className={`flex items-start gap-2 p-2 rounded text-xs ${
                              item.status === 'completed' ? 'bg-green-50' : 'bg-amber-50'
                            }`}
                          >
                            <button
                              onClick={() => toggleActionItem(item.id)}
                              className="mt-0.5"
                            >
                              {item.status === 'completed' ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              ) : (
                                <Circle className="w-4 h-4 text-amber-600" />
                              )}
                            </button>
                            <span className={item.status === 'completed' ? 'line-through text-gray-500' : ''}>
                              {typeof item.text === 'string' ? item.text : JSON.stringify(item.text)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>

              {/* Case Timeline */}
              <Card>
                <CardHeader className="py-3 px-4">
                  <button
                    onClick={() => setExpandedSections(prev => ({ ...prev, caseTimeline: !prev.caseTimeline }))}
                    className="w-full flex items-center justify-between"
                  >
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <History className="w-4 h-4 text-blue-600" />
                      Conversation History
                    </CardTitle>
                    {expandedSections.caseTimeline ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                </CardHeader>
                {expandedSections.caseTimeline && (
                  <CardContent className="px-4 pb-4">
                    {attorneyMessages.length === 0 ? (
                      <p className="text-xs text-gray-500">No conversation history yet.</p>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {attorneyMessages.slice(-10).map((msg, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-xs">
                            <Clock className="w-3 h-3 text-gray-400 mt-0.5" />
                            <div>
                              <span className={`font-medium ${msg.role === 'user' ? 'text-indigo-600' : 'text-amber-600'}`}>
                                {msg.role === 'user' ? 'You' : 'Attorney'}:
                              </span>
                              <span className="text-gray-600 ml-1">
                                {msg.content.substring(0, 50)}...
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-yellow-600" />
                    Quick Actions
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4 space-y-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start text-xs"
                    onClick={() => setInputValue("What are the key Maxims of Equity that apply to my case?")}
                  >
                    <BookOpen className="w-3 h-3 mr-2" />
                    Learn About Equity Maxims
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start text-xs"
                    onClick={() => setInputValue("What remedy steps should I take for my situation?")}
                  >
                    <Gavel className="w-3 h-3 mr-2" />
                    Get Remedy Suggestions
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                    onClick={handleNewAttorneyConversation}
                  >
                    <RefreshCw className="w-3 h-3 mr-2" />
                    Start New Conversation
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* Strategy Brain */}
      {activeTab === 'strategy' && (
        <div className="overflow-y-auto overflow-x-hidden h-[calc(100vh-200px)] w-full max-w-full">
          <StrategyBrain conversationId={conversationId} isPaid={isPaid} refreshTrigger={strategyRefreshTrigger} initialView={strategyInitialView} />
        </div>
      )}

      {/* Mailing Room */}
      {activeTab === 'mailing' && (
        <div className="overflow-auto h-[calc(100vh-200px)]">
          <MailingRoom conversationId={conversationId} isPaid={isPaid} />
        </div>
      )}

      {/* Response Detection */}
      {activeTab === 'detection' && (
        <div className="overflow-auto h-[calc(100vh-200px)]">
          <ResponseDetection conversationId={conversationId} isPaid={isPaid} onDetectionComplete={() => setStrategyRefreshTrigger(prev => prev + 1)} />
        </div>
      )}

      {/* Bill of Complaint Portal */}
      {activeTab === 'complaint' && (
        <div className="overflow-auto h-[calc(100vh-200px)]">
          <ComplaintPortal conversationId={conversationId} embedded={true} />
        </div>
      )}
    </div>
  );
};

export default ChatInterface;
